/**
 * 将 Rapoo 紧凑十进制固件版本转换为显示字符串。
 *
 * 这类协议把版本编码为 `major * 100 + minor`，Qt/1.0 在线驱动均直接显示
 * `% 100` 的十进制结果，不为次版本补前导零。
 */
export function formatCompactFirmwareVersion(value: number, majorAdjustment = 0): string {
  const major = Math.floor(value / 100) + majorAdjustment
  const minor = value % 100
  return `${major}.${minor}`
}

/**
 * 版本号比对函数1
 * @param v1 - 版本号1（支持 "V0.0.1", "0.0.02", "v00.00.12", "1.0" 等）
 * @param v2 - 版本号2
 * @returns -1 表示 v1 < v2, 0 表示相等, 1 表示 v1 > v2
 */
export function compareVersions(v1: string, v2: string): -1 | 0 | 1 {
  /**
   * 标准化版本号字符串，将其转换为数字数组
   * @param version - 原始版本字符串（可能含 v/V 前缀、前导零）
   * @returns 数字数组，如 [0, 0, 1]
   */
  function normalizeVersion(version: string): number[] {
    return version
      .trim()
      .replace(/^[vV]/, '') // 移除开头的 v 或 V
      .split('.')
      .map((part) => {
        const num = parseInt(part, 10)
        return isNaN(num) ? 0 : num // 非数字部分视为 0
      })
  }
  const arr1 = normalizeVersion(v1)
  const arr2 = normalizeVersion(v2)
  const maxLen = Math.max(arr1.length, arr2.length)

  for (let i = 0; i < maxLen; i++) {
    const n1 = i < arr1.length ? arr1[i] : 0
    const n2 = i < arr2.length ? arr2[i] : 0
    if (n1 !== n2) {
      return n1 > n2 ? 1 : -1
    }
  }
  return 0
}

/**
 * 版本比对函数2
 * 注意：此函数会把0.0.0.12与0.12视作同一个版本
 * @param v1 - 版本号1（支持 "V0.0.1", "0.0.02", "v00.00.12", "1.0" 等）
 * @param v2 - 版本号2
 * @returns -1: v1 < v2, 0: 相等, 1: v1 > v2
 */
export function compareVersionsFirmware(v1: string, v2: string): -1 | 0 | 1 {
  /**
   * 去除版本号数组左侧连续的零段
   * 例如 [0, 0, 0, 12] → [12]
   *     [0, 12, 0]   → [12, 0]
   *     [0, 0, 0]    → []  （全零）
   */
  function stripLeadingZeroSegments(segments: number[]): number[] {
    const firstNonZeroIdx = segments.findIndex((seg) => seg !== 0)
    if (firstNonZeroIdx === -1) return [] // 全零
    return segments.slice(firstNonZeroIdx)
  }

  /**
   * 标准化版本号，并去除前导零段
   */
  function normalizeVersion(version: string): number[] {
    const rawSegments = version
      .trim()
      .replace(/^[vV]/, '')
      .split('.')
      .map((part) => {
        const num = parseInt(part, 10)
        return isNaN(num) ? 0 : num
      })
    return stripLeadingZeroSegments(rawSegments)
  }

  const arr1 = normalizeVersion(v1)
  const arr2 = normalizeVersion(v2)
  const maxLen = Math.max(arr1.length, arr2.length)

  for (let i = 0; i < maxLen; i++) {
    const n1 = i < arr1.length ? arr1[i] : 0
    const n2 = i < arr2.length ? arr2[i] : 0
    if (n1 !== n2) {
      return n1 > n2 ? 1 : -1
    }
  }
  return 0
}
